import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { type Dispatch, type SetStateAction, createContext } from "react";
import type { DateRange, DateRangePreset } from "./types";

dayjs.extend(quarterOfYear);

export const DatePickerContext = createContext<{
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}>({
  isOpen: false,
  setIsOpen: () => {},
});

const formatDate = (date: Date): string => {
  return dayjs(date).format("D MMM, YYYY");
};

export const formatDateRange = (range: DateRange | undefined): string | null => {
  if (!range) return null;
  const fromStr = range.from ? formatDate(range.from) : "";
  const toStr = range.to ? formatDate(range.to) : "";
  return `${fromStr} - ${toStr}`;
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return dayjs(date1).isSame(dayjs(date2), "day");
};

export const startOfDay = (date: Date): Date => {
  return dayjs(date).startOf("day").toDate();
};

export const endOfDay = (date: Date): Date => {
  return dayjs(date).endOf("day").toDate();
};

export const getDefaultPresets = (): DateRangePreset[] => {
  const now = dayjs();

  return [
    {
      id: "24h",
      label: "Last 24 hours",
      shortcut: "d",
      dateRange: {
        from: now.subtract(24, "hour").toDate(),
        to: now.toDate(),
      },
    },
    {
      id: "7d",
      label: "Last 7 days",
      shortcut: "w",
      dateRange: {
        from: now.subtract(7, "day").startOf("day").toDate(),
        to: now.endOf("day").toDate(),
      },
    },
    {
      id: "30d",
      label: "Last 30 days",
      shortcut: "t",
      dateRange: {
        from: now.subtract(30, "day").startOf("day").toDate(),
        to: now.endOf("day").toDate(),
      },
    },
    {
      id: "3m",
      label: "Last 3 months",
      dateRange: {
        from: now.subtract(3, "month").startOf("day").toDate(),
        to: now.endOf("day").toDate(),
      },
    },
    {
      id: "12m",
      label: "Last 12 months",
      dateRange: {
        from: now.subtract(12, "month").startOf("day").toDate(),
        to: now.endOf("day").toDate(),
      },
    },
    {
      id: "mtd",
      label: "Month to Date",
      shortcut: "m",
      dateRange: {
        from: now.startOf("month").toDate(),
        to: now.endOf("day").toDate(),
      },
    },
    {
      id: "qtd",
      label: "Quarter to Date",
      shortcut: "q",
      dateRange: {
        from: now.startOf("quarter").toDate(),
        to: now.endOf("day").toDate(),
      },
    },
    {
      id: "ytd",
      label: "Year to Date",
      dateRange: {
        from: now.startOf("year").toDate(),
        to: now.endOf("day").toDate(),
      },
    },
  ];
};

const compareDates = (date1: Date, date2: Date): boolean => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

export const compareRanges = (range1: DateRange, range2: DateRange): boolean => {
  const from1 = range1.from;
  const from2 = range2.from;
  let equalFrom = false;
  if (from1 && from2 && compareDates(from1, from2)) {
    equalFrom = true;
  }

  const to1 = range1.to;
  const to2 = range2.to;
  let equalTo = false;
  if (to1 && to2 && compareDates(to1, to2)) {
    equalTo = true;
  }

  return equalFrom && equalTo;
};
